import Cookies from 'js-cookie'

import { callWithTimeout } from '@/tools/call-with-timeout'
import { parameterize } from '@/tools/string'
import geolocation from '@/tools/geolocation/geolocation'
import getPath from '@/tools/get-path'
import axios from '@/tools/axios/axios'
import { getCurrentUser } from '@/tools/session/get-current-user'

export async function getUserLocation({
  timeout,
  geolocationConcededCallback,
  errorMessage
} = {}) {
  if (timeout) {
    return callWithTimeout(
      fetch.bind(this, { geolocationConcededCallback }),
      timeout,
      errorMessage
    )
  } else {
    return fetch({ geolocationConcededCallback })
  }
}

async function fetch({ geolocationConcededCallback }) {
  // Prefetch location from user IP and/or zipcode in order to avoid request cascade.
  // The response has cache control configured to enable disk cache.
  const promiseLocationFromUserIpAndZipcode = getLocationFromUserIpAndZipcode()
  const addressLocation = await getLocationFromUserAddress()

  if (addressLocation?.city) {
    return parameterizeUserLocation(addressLocation)
  }

  const { zipcodeLocation, ipLocation } = await promiseLocationFromUserIpAndZipcode

  if (zipcodeLocation?.city) {
    return parameterizeUserLocation(zipcodeLocation)
  }

  const userGeolocation = getUserGeolocation({ geolocationConcededCallback })

  if (userGeolocation?.city) {
    return parameterizeUserLocation(userGeolocation)
  }

  return parameterizeUserLocation(ipLocation)
}

export async function getLocationFromUserAddress() {
  let [user, promisedRevalidatedUser] = getCurrentUser()

  if (!user?.hasOwnProperty('isSignedIn')) {
    user = await promisedRevalidatedUser
  }

  if (user?.address?.state && user?.address?.city) {
    return {
      city: user.address.city,
      state: user.address.state
    }
  }
}

export async function getLocationFromUserIpAndZipcode() {
  const zipcode = Cookies.get('custom_zipcode')
  const endpoint = getPath('users-location-endpoint')

  const location = await axios.get(endpoint, {
    params: {
      zipcode: zipcode
    },
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'X-EnjoeiApi-Version': 'v1'
    }
  })

  return {
    zipcodeLocation: location?.data?.addresses?.zipcode,
    ipLocation: location?.data?.addresses?.ip
  }
}

export function getUserGeolocation({ geolocationConcededCallback }) {
  const location = geolocation.get()

  if (location?.city) return location

  if (!geolocation.hasAttempted() && geolocationConcededCallback) {
    geolocation.request().then(() => geolocationConcededCallback())
  }
}

export function parameterizeUserLocation(location) {
  if (!location?.city || !location?.state)
    return {
      city: '',
      state: ''
    }

  return {
    city: parameterize(location.city),
    state: parameterize(location.state)
  }
}
